import { useCallback, useId, useState } from 'react';

export type DropdownMenuClick = (
  event: React.MouseEvent<HTMLButtonElement>,
) => void;

export type DropdownMenuRenderTriggerButtonParams = {
  id: string;
  onClick: DropdownMenuClick;
  'aria-controls': string | undefined;
  'aria-haspopup': boolean;
  'aria-expanded': boolean | undefined;
};

/** @internal Hook used in DropdownMenu to include the default props required. */
export const useDropdownMenu = () => {
  const id = useId();
  const [dropdownAnchorEl, setDropdownAnchorEl] =
    useState<null | HTMLButtonElement>(null);
  const dropdownIsOpen = Boolean(dropdownAnchorEl);
  const dropdownMenuTriggerId = `${id}-trigger`;
  const dropdownMenuId = `${id}-menu`;

  const handleDropdownMenuClick: DropdownMenuClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setDropdownAnchorEl(event.currentTarget);
    },
    [],
  );

  const handleDropdownMenuClose = useCallback(() => {
    setDropdownAnchorEl(null);
  }, []);

  const dropdownMenuTriggerButtonParams: DropdownMenuRenderTriggerButtonParams =
    {
      id: dropdownMenuTriggerId,
      onClick: handleDropdownMenuClick,
      'aria-controls': dropdownIsOpen ? dropdownMenuId : undefined,
      'aria-haspopup': true,
      'aria-expanded': dropdownIsOpen ? true : undefined,
    };

  const dropdownMenuListParams = {
    'aria-labelledby': dropdownMenuTriggerId,
  };

  return {
    dropdownAnchorEl,
    dropdownMenuTriggerId,
    dropdownMenuId,
    dropdownIsOpen,
    dropdownMenuTriggerButtonParams,
    dropdownMenuListParams,
    handleDropdownMenuClick,
    handleDropdownMenuClose,
  };
};
